import {
    DISPUTE_AMOUNT_THRESHOLD,
    INVESTIGATION_STATUS,
    PAYMENT_METHOD_TYPES,
    PAYMENT_STATUS,
    STRIPE_DISPUTE_STATUS,
    STRIPE_EVENT_TYPES,
    STRIPE_PAYOUT_ERRORS_AND_RESPONSES_HTML,
    TAX_BEHAVIOUR,
} from '@/lib/constants';
import { PaymentDataResponse, PaymentStatus } from '@/types/payments';
import { PAYMENT_GATEWAY_MAPPING } from './constants/transactionConstants';
import { ProductPayment } from './types/transactionTypes';
import { formatCurrency, formatDateShort, isDateInPast } from '@/lib';
import { formatDistanceToNowStrict } from 'date-fns';

export function formatPaymentData(rawPaymentData: PaymentDataResponse): ProductPayment {
    const paymentData: ProductPayment = rawPaymentData;
    const isAffiliateTransaction = rawPaymentData.data?.affiliate_share?.is_qualified_affiliate_transaction;
    paymentData.revenue = rawPaymentData.amount;

    if (paymentData.pos_currency !== paymentData.currency_base) {
        paymentData.netAmountFormatted = formatCurrency(
            rawPaymentData.amount_base - rawPaymentData.processing_fee,
            paymentData.currency_base
        );
    } else {
        paymentData.netAmountFormatted = formatCurrency(rawPaymentData.amount - rawPaymentData.processing_fee);
    }
    paymentData.amountBaseFormatted = formatCurrency(rawPaymentData.amount_base, paymentData.currency_base);
    paymentData.processingFeeFormatted = formatCurrency(rawPaymentData.processing_fee, paymentData.currency_base);
    paymentData.amountFormatted = formatCurrency(rawPaymentData.amount, paymentData.pos_currency);
    if (isAffiliateTransaction && paymentData.product_type !== 'affiliate-fee') {
        paymentData.subType = `via @${paymentData.affiliated_username}`;
    } else if (paymentData.data?.order_bump && paymentData.data?.order_bump_parent_page) {
        paymentData.subType = 'Order Bump';
    } else if (paymentData.pos_data?.tags?.funnel_page_id) {
        paymentData.subType = 'Funnel';
    }
    return paymentData;
}

// Payment Utilities
export function getPaymentMethodType(method?: string): string {
    switch (method) {
        case PAYMENT_METHOD_TYPES.KLARNA:
            return 'Klarna ';
        case PAYMENT_METHOD_TYPES.AFTERPAY:
            return 'Afterpay ';
        case PAYMENT_METHOD_TYPES.LINK:
            return 'Link ';
        case PAYMENT_METHOD_TYPES.IDEAL:
            return 'Ideal ';
        case PAYMENT_METHOD_TYPES.PAYPAL:
            return 'Paypal ';
        case PAYMENT_METHOD_TYPES.CARD:
        default:
            return '';
    }
}

export function getPaymentGatewayDisplayName(paymentGateway?: string): string {
    return PAYMENT_GATEWAY_MAPPING[paymentGateway || ''] || '';
}

export function calculatePaymentAmounts(paymentData: ProductPayment) {
    const posGrossAmountNumber = paymentData?.pos_data?.gross_amount?.value ?? 0;
    const netAmount = paymentData?.pos_data?.net_amount?.value ?? 0;
    const applicationFee = paymentData?.pos_data?.application_fee;
    const feeAmount = paymentData?.pos_data?.fee_amount?.value;
    const taxAmount = paymentData?.pos_data?.tax_amount?.value;
    const revenue = paymentData.revenue || 0;

    // Calculate core amounts
    const creatorAmount = applicationFee ? posGrossAmountNumber - applicationFee : netAmount;
    const preTaxRevenue = revenue - (taxAmount ?? 0);

    // Format amounts
    const posNetRevenue =
        netAmount === 0
            ? undefined
            : formatCurrency(creatorAmount, paymentData?.pos_data?.net_amount?.currency ?? 'USD');

    const posFeeAmount =
        !feeAmount && !applicationFee
            ? undefined
            : formatCurrency(
                  (feeAmount || applicationFee) as number,
                  paymentData?.pos_data?.fee_amount?.currency ?? 'USD'
              );

    const posRevenue = formatCurrency(preTaxRevenue, paymentData.currency || 'USD');

    const posTaxRevenue = !taxAmount
        ? undefined
        : formatCurrency(taxAmount as number, paymentData?.pos_data?.tax_amount?.currency ?? 'USD');

    const posGrossAmount = formatCurrency(posGrossAmountNumber, paymentData?.pos_data?.gross_amount?.currency ?? 'USD');

    return {
        posGrossAmount,
        posNetRevenue,
        posFeeAmount,
        posRevenue,
        posTaxRevenue,
        creatorAmount,
        preTaxRevenue,
    };
}

export function isStatusPaid(status: PaymentStatus): boolean {
    return [
        PAYMENT_STATUS.CREATOR_FULFILLED,
        PAYMENT_STATUS.PAID_NOT_FULFILLED,
        PAYMENT_STATUS.REFUNDED,
        PAYMENT_STATUS.DISPUTED,
    ].includes(status);
}

export function isStatusDisputed(status: PaymentStatus): boolean {
    return status === PAYMENT_STATUS.DISPUTED;
}

export function isDisputeWon(paymentData: ProductPayment): boolean {
    return !!(paymentData.data?.dispute_id && paymentData.data?.dispute_status === STRIPE_DISPUTE_STATUS.WON);
}

export function formatPreConversionAmountDisplay(paymentData: ProductPayment): string {
    const taxBehavior = paymentData?.pos_data?.tax_amount?.tax_behavior;
    const { posTaxRevenue } = calculatePaymentAmounts(paymentData);

    return taxBehavior === TAX_BEHAVIOUR.INCLUSIVE
        ? `${paymentData.amountFormatted} (incl. ${posTaxRevenue} tax)`
        : `${paymentData.amountFormatted}`;
}

export function getPaymentDisplayFlags(paymentData: ProductPayment) {
    const taxBehavior = paymentData?.pos_data?.tax_amount?.tax_behavior;
    const taxAmount = paymentData?.pos_data?.tax_amount?.value;
    const posGrossAmountNumber = paymentData?.pos_data?.gross_amount?.value ?? 0;

    const showTaxAmount = taxBehavior === TAX_BEHAVIOUR.EXCLUSIVE && (taxAmount || 0) > 0;
    const showGrossAmount =
        posGrossAmountNumber !== 0 && paymentData?.amount !== paymentData?.pos_data?.gross_amount?.value;

    return {
        showTaxAmount,
        showGrossAmount,
    };
}

export function formatEventData(stripeEvent: any, paymentData: ProductPayment) {
    const eventType = Object.values(STRIPE_EVENT_TYPES).find(t => t.STRIPE_CODE === stripeEvent.type);
    const payoutDetails = paymentData.pos_data?.payout_details || {};
    const failureCode = payoutDetails?.failure_code || '';
    let payoutErrorMessage = null;

    const upperFailureCode = failureCode.toUpperCase();
    if (upperFailureCode in STRIPE_PAYOUT_ERRORS_AND_RESPONSES_HTML) {
        // More readable stripe error messages: https://stripe.com/docs/api/payouts/object#payout_object-failure_code
        payoutErrorMessage =
            STRIPE_PAYOUT_ERRORS_AND_RESPONSES_HTML[
                upperFailureCode as keyof typeof STRIPE_PAYOUT_ERRORS_AND_RESPONSES_HTML
            ];
    }

    const out = {
        subtitle: formatDateShort(stripeEvent.created * 1000, false, true),
        icon: eventType?.ICON,
        title: '',
    };

    if (stripeEvent.type === STRIPE_EVENT_TYPES.PAYMENT_SUCCEEDED.STRIPE_CODE) {
        out.title = `Payment succeeded.`;
    } else if (stripeEvent.type === STRIPE_EVENT_TYPES.PAYMENT_EXPECTED_AVAILABLE_TIME.STRIPE_CODE) {
        out.title = `Payment available for Cash Out on ${formatDateShort(stripeEvent.expected_available_time * 1000, true, false)}.`;
        out.subtitle = formatDateShort(stripeEvent.expected_available_time * 1000, false, true);
        out.icon =
            Date.now() > stripeEvent.expected_available_time * 1000 ? eventType?.ICON_SUCCESS : eventType?.ICON_WAITING;
    } else if (stripeEvent.type === STRIPE_EVENT_TYPES.PAYOUT_CREATED.STRIPE_CODE) {
        if (paymentData.bank_name) {
            out.title = `Payout initiated to the bank account ending in ${paymentData.last4} at ${paymentData.bank_name}.`;
        } else if (paymentData.brand && paymentData.brand !== 'Unknown') {
            out.title = `Payout initiated to the ${paymentData.brand} card ending in ${paymentData.last4}.`;
        } else {
            out.title = `Payout initiated.`;
        }
    } else if (stripeEvent.type === STRIPE_EVENT_TYPES.PAYOUT_UPDATED.STRIPE_CODE) {
        out.title = `Payout expected to arrive on ${formatDateShort(stripeEvent.created * 1000, true, false)}.`;
    } else if (stripeEvent.type === STRIPE_EVENT_TYPES.PAYOUT_PAID.STRIPE_CODE) {
        if (paymentData.bank_name) {
            out.title = `Payout should have arrived in the bank account ending in ${paymentData.last4} at ${paymentData.bank_name}.`;
        } else if (paymentData.brand && paymentData.brand !== 'Unknown') {
            out.title = `Payout should have arrived in the ${paymentData.brand} card ending in ${paymentData.last4}.`;
        } else {
            out.title = `Payout should have arrived.`;
        }
    } else if (stripeEvent.type === STRIPE_EVENT_TYPES.PAYOUT_FAILED.STRIPE_CODE) {
        out.title = payoutErrorMessage ?? '';
    }
    return out;
}
function _initialDisputeBannerMessage(paymentData: ProductPayment) {
    if (paymentData.data?.dispute_reason === 'product_not_received') {
        return 'Unfortunately, your customer disputed this payment, claiming: "The product was not received".';
    }
    if (paymentData.data?.dispute_reason === 'product_unacceptable') {
        return 'Unfortunately, Your customer disputed this payment, claiming: "The product is not as described".';
    }

    return 'Unfortunately, your customer disputed this payment.';
}
export function disputeBannerContent(paymentData: ProductPayment, isDisputeSubmitted: boolean) {
    const disputeStatus = paymentData.data.dispute_status;
    const disputeId = paymentData.data.dispute_id;
    const isPaymentDisputeWon = isDisputeWon(paymentData);
    const doesDisputeNeedResponse =
        disputeStatus === STRIPE_DISPUTE_STATUS.NEEDS_RESPONSE ||
        disputeStatus === STRIPE_DISPUTE_STATUS.WARNING_NEEDS_RESPONSE;
    const isDisputeUnderReview =
        disputeStatus === STRIPE_DISPUTE_STATUS.UNDER_REVIEW ||
        disputeStatus === STRIPE_DISPUTE_STATUS.WARNING_UNDER_REVIEW;
    const posRevenueNoCurrency = (paymentData.revenue ?? 0) - (paymentData?.pos_data?.tax_amount?.value ?? 0);

    const isAutoDisputeResolutionAllowed =
        posRevenueNoCurrency < DISPUTE_AMOUNT_THRESHOLD ||
        paymentData.investigation_status !== INVESTIGATION_STATUS.PENDING;

    const isDisputeDueByPast =
        paymentData.data?.evidence_due_by && isDateInPast(paymentData.data?.evidence_due_by * 1000);

    const daysInMs = 75 * 24 * 60 * 60; // 75 days in ms
    const disputeResultBy = paymentData.data?.evidence_due_by
        ? formatDateShort(new Date((paymentData.data?.evidence_due_by + daysInMs) * 1000), true)
        : '';
    const formatDistance = (timeInMs: number) => {
        return formatDistanceToNowStrict(timeInMs, { addSuffix: false });
    };
    const disputeDueBy = isDisputeDueByPast
        ? ''
        : paymentData.data?.evidence_due_by
          ? formatDistance(paymentData.data?.evidence_due_by * 1000)
          : '';

    if (disputeId) {
        if (!isDisputeSubmitted && doesDisputeNeedResponse && disputeDueBy) {
            const prefix = `<b>${_initialDisputeBannerMessage(paymentData)}</b>`;
            const followUp = isAutoDisputeResolutionAllowed
                ? `Please share compelling evidence by responding within <b>${disputeDueBy}</b>.`
                : `Please email <a href="mailto:friends@stanwith.me">friends@stanwith.me</a> for assistance.`;
            return `${prefix} ${followUp}`;
        }
        if (isDisputeUnderReview && disputeResultBy) {
            return `<b>Your dispute evidence has been submitted.</b> We expect a decision by <b>${disputeResultBy}.</b>`;
        }
        if (disputeStatus === STRIPE_DISPUTE_STATUS.LOST) {
            return "<b>This payment was disputed and was lost.</b> Unfortunately the customer's bank decided in their favor.";
        }
        if (isPaymentDisputeWon) {
            return '<b>You won this dispute!</b>';
        }
    }
    return `<b>${_initialDisputeBannerMessage(paymentData)}</b>`;
}

export const isDisputeCardVisible = (paymentData: ProductPayment, isDisputeSubmitted: boolean) => {
    const disputeStatus = paymentData.data.dispute_status;
    const disputeId = paymentData.data.dispute_id;

    const doesDisputeNeedResponse =
        disputeStatus === STRIPE_DISPUTE_STATUS.NEEDS_RESPONSE ||
        disputeStatus === STRIPE_DISPUTE_STATUS.WARNING_NEEDS_RESPONSE;

    const posRevenueNoCurrency = (paymentData.revenue ?? 0) - (paymentData?.pos_data?.tax_amount?.value ?? 0);

    const isAutoDisputeResolutionAllowed =
        posRevenueNoCurrency < DISPUTE_AMOUNT_THRESHOLD ||
        paymentData.investigation_status !== INVESTIGATION_STATUS.PENDING;

    const isDisputeDueByPast =
        paymentData.data?.evidence_due_by && isDateInPast(paymentData.data?.evidence_due_by * 1000);

    return (
        !isDisputeDueByPast &&
        !isDisputeSubmitted &&
        disputeId &&
        doesDisputeNeedResponse &&
        isAutoDisputeResolutionAllowed
    );
};
